import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Slider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import CropBoxOverlay from '../CropBoxOverlay';
import { getVideoBaseName } from '../DataProvider';

const minGap = 0.5; // Minimum 0.5 second gap between start and end
const ffmpeg = new FFmpeg();

/**
 * VideoTrimDialog - Dialog for trimming video segments
 * 
 * Props:
 * - open: boolean - controls dialog visibility
 * - onClose: () => void - callback when dialog is closed
 * - videoRef: ref - reference to the video element
 * - videoSrc: string - source URL of the video
 */
const VideoTrimDialog = ({ open, onClose, videoRef, videoSrc }) => {
  const [duration, setDuration] = useState(0);
  const [trimRange, setTrimRange] = useState([0, 0]); // [start, end] as array for range slider
  const [enableCropping, setEnableCropping] = useState(false);
  const [trimming, setTrimming] = useState(false);
  const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
  const [ffmpegLoading, setFfmpegLoading] = useState(false);
  const [trimError, setTrimError] = useState(null);
  const [trimmedUrl, setTrimmedUrl] = useState(null);
  const [cropBox, setCropBox] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [previewTime, setPreviewTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const previewVideoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const sliderContainerRef = useRef(null);

  // Derived values
  const trimStart = trimRange[0];
  const trimEnd = trimRange[1];
  const selectedDuration = trimEnd - trimStart;

  const loadFfmpeg = useCallback(async () => {
    if (ffmpeg.loaded) {
      setFfmpegLoaded(true);
      return true;
    }
    if (ffmpegLoading) {
      // Wait for existing load to complete
      return new Promise((resolve) => {
        const checkLoaded = setInterval(() => {
          if (!ffmpegLoading) {
            clearInterval(checkLoaded);
            resolve(ffmpeg.loaded);
          }
        }, 100);
      });
    }

    setFfmpegLoading(true);
    try {
      await ffmpeg.load();
      setFfmpegLoaded(true);
      return true;
    } catch (err) {
      console.error('FFmpeg load error:', err);
      setTrimError('Failed to load trimming engine: ' + err.message);
      return false;
    } finally {
      setFfmpegLoading(false);
    }
  }, [ffmpegLoading]);

  // Auto-load FFmpeg when dialog opens
  useEffect(() => {
    if (open && !ffmpegLoaded && !ffmpegLoading) {
      loadFfmpeg();
    }
  }, [open, ffmpegLoaded, ffmpegLoading, loadFfmpeg]);

  // Initialize state when dialog opens
  useEffect(() => {
    if (open && videoRef?.current) {
      const video = videoRef.current;
      const dur = video.duration || 0;
      setDuration(dur);
      setTrimRange([0, dur]);
      setTrimError(null);
      setTrimmedUrl(null);
      setPreviewTime(0);
      setIsPlaying(false);
      setEnableCropping(false);
      // Reset crop box - will be initialized when video loads in dialog
      setCropBox({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, [open, videoRef]);

  // Initialize crop box when preview video loads
  const initializeCropBox = () => {
    if (previewVideoRef.current && videoContainerRef.current) {
      const container = videoContainerRef.current;
      const rect = container.getBoundingClientRect();
      const video = previewVideoRef.current;
      
      // Set crop box to full video area with 25px inset
      const inset = 25;
      setCropBox({
        x: inset,
        y: inset,
        width: Math.max(rect.width - inset * 2, 50),
        height: Math.max(rect.height - inset * 2, 50),
        // Store scale factors for ffmpeg
        parentWidth: rect.width,
        parentHeight: rect.height,
        scaleX: video.videoWidth / rect.width,
        scaleY: video.videoHeight / rect.height,
      });
    }
  };

  // Handle crop box changes while preserving scale factors
  const handleCropBoxChange = (newBox) => {
    setCropBox(prev => ({
      ...prev,
      ...newBox,
    }));
  };

  const handleTrim = async () => {
    setTrimError(null);
    setTrimming(true);
    setTrimmedUrl(null);
    try {
      // Ensure FFmpeg is loaded
      const loaded = await loadFfmpeg();
      if (!loaded) {
        setTrimError('Failed to load trimming engine.');
        setTrimming(false);
        return;
      }
      
      const inputName = 'input.mp4';
      const outputName = 'output_trimmed.mp4';
      
      // Fetch video and write to FFmpeg filesystem
      const videoBuffer = await fetch(videoSrc).then((r) => r.arrayBuffer());
      await ffmpeg.writeFile(inputName, new Uint8Array(videoBuffer));
      
      // Calculate crop values in video pixel coordinates if cropping is enabled
      let ffmpegArgs;
      let cropNeeded = false;
      let cropX, cropY, cropW, cropH;
      if (
        enableCropping &&
        cropBox &&
        cropBox.width > 0 &&
        cropBox.height > 0 &&
        (Math.abs(cropBox.x) > 2 ||
          Math.abs(cropBox.y) > 2 ||
          Math.abs(cropBox.width - cropBox.parentWidth) > 2 ||
          Math.abs(cropBox.height - cropBox.parentHeight) > 2)
      ) {
        cropNeeded = true;
        cropX = Math.round(cropBox.x * cropBox.scaleX);
        cropY = Math.round(cropBox.y * cropBox.scaleY);
        cropW = Math.round(cropBox.width * cropBox.scaleX);
        cropH = Math.round(cropBox.height * cropBox.scaleY);
      }
      
      if (cropNeeded) {
        ffmpegArgs = [
          '-ss', String(trimStart),
          '-to', String(trimEnd),
          '-i', inputName,
          '-filter:v', `crop=${cropW}:${cropH}:${cropX}:${cropY}`,
          '-c:a', 'copy',
          '-f', 'mp4',
          '-movflags', '+faststart',
          outputName,
        ];
      } else {
        ffmpegArgs = [
          '-ss', String(trimStart),
          '-to', String(trimEnd),
          '-i', inputName,
          '-c', 'copy',
          '-f', 'mp4',
          '-movflags', '+faststart',
          outputName,
        ];
      }
      
      console.log('FFmpeg args:', ffmpegArgs);
      await ffmpeg.exec(ffmpegArgs);
      
      let data;
      try {
        data = await ffmpeg.readFile(outputName);
      } catch (err) {
        console.error('Failed to read output file:', err);
        setTrimError('Trimming failed: output file not found. The video format may not be supported.');
        setTrimming(false);
        return;
      }
      
      const url = URL.createObjectURL(new Blob([data.buffer], { type: 'video/mp4' }));
      setTrimmedUrl(url);
    } catch (e) {
      console.error('Trim error:', e);
      setTrimError(`Trimming failed: ${e.message || 'Unknown error. Check browser console for details.'}`);
    }
    setTrimming(false);
  };

  const formatTime = (sec) => {
    if (!sec || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Format duration in a friendly way
  const formatDuration = (sec) => {
    if (!sec || isNaN(sec)) return '0s';
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    if (m > 0) {
      return s > 0 ? `${m}m ${s}s` : `${m}m`;
    }
    return `${s}s`;
  };

  // Generate download filename based on original video
  const getDownloadFilename = () => {
    return `${getVideoBaseName(videoSrc)}_trimmed.mp4`;
  };

  // Track which thumb is being dragged
  const draggingThumbRef = useRef(null);

  // Handle range slider change (updates UI only, no video seek during drag)
  const handleRangeChange = (_, newValue, activeThumb) => {
    const [newStart, newEnd] = newValue;
    // Ensure minimum gap
    if (newEnd - newStart >= minGap) {
      setTrimRange(newValue);
      draggingThumbRef.current = activeThumb; // 0 = start thumb, 1 = end thumb
    }
  };

  // Handle slider change committed (seek video only when user finishes dragging)
  const handleRangeChangeCommitted = (_, newValue) => {
    const [newStart, newEnd] = newValue;
    // Seek to the position that was adjusted
    if (draggingThumbRef.current === 0) {
      seekPreviewTo(newStart);
    } else {
      seekPreviewTo(newEnd);
    }
    draggingThumbRef.current = null;
  };

  // Seek preview video to a specific time
  const seekPreviewTo = (time) => {
    if (previewVideoRef.current) {
      previewVideoRef.current.currentTime = time;
      setPreviewTime(time);
    }
  };

  // Calculate time from mouse position on slider
  const getTimeFromPosition = (clientX) => {
    if (!sliderContainerRef.current || !duration) return null;
    const rect = sliderContainerRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percent * duration;
  };

  // Handle progress indicator drag start
  const handleProgressDragStart = (e) => {
    e.preventDefault();
    setIsDraggingProgress(true);
    
    const handleDragMove = (moveEvent) => {
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const time = getTimeFromPosition(clientX);
      if (time !== null) {
        setPreviewTime(time);
      }
    };
    
    const handleDragEnd = (endEvent) => {
      const clientX = endEvent.changedTouches ? endEvent.changedTouches[0].clientX : endEvent.clientX;
      const time = getTimeFromPosition(clientX);
      if (time !== null) {
        seekPreviewTo(time);
      }
      setIsDraggingProgress(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
    
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDragMove);
    document.addEventListener('touchend', handleDragEnd);
  };

  // Handle preview video time update - constrain to trim range
  const handlePreviewTimeUpdate = () => {
    if (previewVideoRef.current) {
      const currentTime = previewVideoRef.current.currentTime;
      setPreviewTime(currentTime);
      
      // If playing and we've reached the trim end, loop back to trim start
      if (isPlaying && currentTime >= trimEnd) {
        previewVideoRef.current.currentTime = trimStart;
        setPreviewTime(trimStart);
      }
    }
  };

  // Toggle play/pause - start from trim start if outside range
  const togglePlayPause = () => {
    if (previewVideoRef.current) {
      if (isPlaying) {
        previewVideoRef.current.pause();
      } else {
        // If current time is outside trim range, start from trim start
        const currentTime = previewVideoRef.current.currentTime;
        if (currentTime < trimStart || currentTime >= trimEnd) {
          previewVideoRef.current.currentTime = trimStart;
          setPreviewTime(trimStart);
        }
        previewVideoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle video ended
  const handleVideoEnded = () => {
    setIsPlaying(false);
    // Reset to trim start
    if (previewVideoRef.current) {
      previewVideoRef.current.currentTime = trimStart;
      setPreviewTime(trimStart);
    }
  };

  const handleClose = () => {
    // Clean up blob URL when closing
    if (trimmedUrl) {
      URL.revokeObjectURL(trimmedUrl);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '50vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Trim Video</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Video Preview */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box 
              ref={videoContainerRef}
              sx={{ 
                position: 'relative', 
                bgcolor: 'black', 
                borderRadius: 1, 
                overflow: 'visible',
                display: 'inline-block',
              }}
            >
              <video
                ref={previewVideoRef}
                src={videoSrc}
                onTimeUpdate={handlePreviewTimeUpdate}
                onEnded={handleVideoEnded}
                onLoadedMetadata={() => {
                  if (previewVideoRef.current) {
                    const dur = previewVideoRef.current.duration;
                    setDuration(dur);
                    setTrimRange([0, dur]);
                    // Initialize crop box after video loads
                    setTimeout(initializeCropBox, 100);
                  }
                }}
                style={{ maxHeight: 280, display: 'block' }}
              />
              {/* Crop overlay */}
              {enableCropping && cropBox.width > 0 && (
                <CropBoxOverlay
                  cropBox={cropBox}
                  onCropBoxChange={handleCropBoxChange}
                  videoRef={videoContainerRef}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Range Slider with Play Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, mb: 1 }}>
          <IconButton onClick={togglePlayPause} color="primary" size="small">
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          <Box 
            ref={sliderContainerRef}
            sx={{ flex: 1, position: 'relative' }}
          >
            <Slider
              value={trimRange}
              onChange={handleRangeChange}
              onChangeCommitted={handleRangeChangeCommitted}
              min={0}
              max={duration}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={formatTime}
              disableSwap
              sx={{
                '& .MuiSlider-track': {
                  height: 8,
                },
                '& .MuiSlider-rail': {
                  height: 8,
                  opacity: 0.3,
                },
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                },
              }}
            />
            {/* Draggable progress indicator */}
            {duration > 0 && (
              <Box
                onMouseDown={handleProgressDragStart}
                onTouchStart={handleProgressDragStart}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: `calc(${(previewTime / duration) * 100}%)`,
                  transform: 'translate(-50%, -50%)',
                  width: 12,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'ew-resize',
                  zIndex: 15,
                  transition: isDraggingProgress ? 'none' : 'left 0.1s',
                  '&:hover > div': {
                    width: 5,
                    bgcolor: 'error.dark',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 3,
                    height: 20,
                    bgcolor: 'error.main',
                    borderRadius: 1,
                    transition: 'width 0.1s, background-color 0.1s',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        
        {/* Selected start/end times under slider */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, mb: 2, ml: 5 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 500 }}>
            {formatTime(trimStart)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Selected: {formatDuration(selectedDuration)}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 500 }}>
            {formatTime(trimEnd)}
          </Typography>
        </Box>

        {/* Options */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={enableCropping}
                onChange={(_, checked) => {
                  setEnableCropping(checked);
                  // Re-initialize crop box when enabling
                  if (checked) {
                    setTimeout(initializeCropBox, 50);
                  }
                }}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Enable cropping (adjust the region on the video above)
              </Typography>
            }
          />
        </Box>

        {/* Error */}
        {trimError && (
          <Alert severity="error" sx={{ mb: 2 }}>{trimError}</Alert>
        )}

        {/* Trimmed Video Result */}
        {trimmedUrl && (
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="success.contrastText">
              Trimmed Video Ready
            </Typography>
            <video
              src={trimmedUrl}
              controls
              style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>
          Close
        </Button>
        {trimmedUrl ? (
          <Button
            component="a"
            href={trimmedUrl}
            download={getDownloadFilename()}
            variant="contained"
          >
            Download
          </Button>
        ) : (
          <Button
            onClick={handleTrim}
            variant="contained"
            disabled={trimming || ffmpegLoading || selectedDuration < minGap}
            startIcon={(trimming || ffmpegLoading) ? <CircularProgress size={16} /> : null}
          >
            {ffmpegLoading ? 'Loading...' : trimming ? 'Trimming...' : 'Trim Video'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VideoTrimDialog;
